import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { 
  Leaf, MapPin, Ruler, Sun, Trees, Bug, Sparkles,
  ArrowLeft, ArrowRight, Check, Loader2, Home, Navigation
} from "lucide-react";
import GetStartedChecklist from "../components/GetStartedChecklist";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix for Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Map click handler component
const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Map center updater component
const MapCenterUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
};

const GardenWizard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [solarData, setSolarData] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    latitude: 40.7128,
    longitude: -74.0060,
    width: 10,
    length: 10,
    height: 3,
    aesthetic: "food"
  });

  const steps = [
    { number: 1, title: "Location", icon: <MapPin className="w-5 h-5" /> },
    { number: 2, title: "Dimensions", icon: <Ruler className="w-5 h-5" /> },
    { number: 3, title: "Aesthetic", icon: <Trees className="w-5 h-5" /> },
    { number: 4, title: "Generate", icon: <Sparkles className="w-5 h-5" /> }
  ];

  const aestheticOptions = [
    {
      value: "food",
      title: "Food Forest",
      description: "Edible gardens with vegetables, fruits, and herbs",
      icon: <Leaf className="w-8 h-8" />,
      color: "border-green-500 bg-green-50"
    },
    {
      value: "serenity",
      title: "Serenity Garden",
      description: "Peaceful spaces with ornamental plants and water features",
      icon: <Sun className="w-8 h-8" />,
      color: "border-blue-500 bg-blue-50"
    },
    {
      value: "wildlife",
      title: "Pollinator Paradise",
      description: "Attract bees, butterflies, and beneficial wildlife",
      icon: <Bug className="w-8 h-8" />,
      color: "border-amber-500 bg-amber-50"
    },
    {
      value: "freedom",
      title: "Freedom Garden",
      description: "Indoor growing, seedlings & winter zone solutions",
      icon: <Home className="w-8 h-8" />,
      color: "border-purple-500 bg-purple-50"
    },
    {
      value: "mixed",
      title: "Mixed Garden",
      description: "Balance of food, beauty, and wildlife habitat",
      icon: <Trees className="w-8 h-8" />,
      color: "border-teal-500 bg-teal-50"
    }
  ];

  // Fetch solar data when latitude changes
  useEffect(() => {
    const fetchSolar = async () => {
      try {
        const response = await axios.post(`${API}/solar-calculation`, {
          latitude: formData.latitude,
          longitude: formData.longitude,
          day_of_year: 172
        });
        setSolarData(response.data);
      } catch (error) {
        console.error("Failed to fetch solar data:", error);
      }
    };
    
    if (formData.latitude) {
      fetchSolar();
    }
  }, [formData.latitude, formData.longitude]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'latitude' || name === 'longitude' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSliderChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value[0]
    }));
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGenerate = async () => {
    if (!formData.name || !formData.location) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsGenerating(true);
    
    try {
      const response = await axios.post(`${API}/garden-plans`, formData);
      toast.success("Garden plan created successfully!");
      navigate(`/dashboard/${response.data.id}`);
    } catch (error) {
      console.error("Failed to generate plan:", error);
      toast.error("Failed to generate garden plan. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <Label htmlFor="name" className="text-base font-medium">Garden Name</Label>
              <Input
                id="name"
                name="name"
                placeholder="My Permaculture Paradise"
                value={formData.name}
                onChange={handleInputChange}
                className="mt-2 input-organic"
                data-testid="garden-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="location" className="text-base font-medium">Location / Address</Label>
              <Input
                id="location"
                name="location"
                placeholder="123 Garden St, City, Country"
                value={formData.location}
                onChange={handleInputChange}
                className="mt-2 input-organic"
                data-testid="location-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="latitude" className="text-base font-medium">Latitude</Label>
                <Input
                  id="latitude"
                  name="latitude"
                  type="number"
                  step="0.0001"
                  value={formData.latitude}
                  onChange={handleInputChange}
                  className="mt-2 input-organic font-mono"
                  data-testid="latitude-input"
                />
              </div>
              <div>
                <Label htmlFor="longitude" className="text-base font-medium">Longitude</Label>
                <Input
                  id="longitude"
                  name="longitude"
                  type="number"
                  step="0.0001"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  className="mt-2 input-organic font-mono"
                  data-testid="longitude-input"
                />
              </div>
            </div>

            {/* Interactive Map */}
            <div className="h-64 rounded-xl overflow-hidden border border-border relative">
              <MapContainer
                center={[formData.latitude, formData.longitude]}
                zoom={13}
                style={{ height: "100%", width: "100%" }}
                data-testid="location-map"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[formData.latitude, formData.longitude]} />
                <MapClickHandler 
                  onLocationSelect={(lat, lng) => {
                    setFormData(prev => ({
                      ...prev,
                      latitude: parseFloat(lat.toFixed(6)),
                      longitude: parseFloat(lng.toFixed(6))
                    }));
                    toast.success("Location selected!");
                  }} 
                />
                <MapCenterUpdater center={[formData.latitude, formData.longitude]} />
              </MapContainer>
              
              {/* Use My Location Button */}
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="absolute bottom-3 right-3 z-[1000] shadow-lg"
                onClick={() => {
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                      (position) => {
                        setFormData(prev => ({
                          ...prev,
                          latitude: parseFloat(position.coords.latitude.toFixed(6)),
                          longitude: parseFloat(position.coords.longitude.toFixed(6))
                        }));
                        toast.success("Using your current location!");
                      },
                      () => toast.error("Could not get your location")
                    );
                  } else {
                    toast.error("Geolocation not supported");
                  }
                }}
                data-testid="use-my-location-btn"
              >
                <Navigation className="w-4 h-4 mr-1" />
                Use My Location
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground text-center">
              Click on the map to select a location, or enter coordinates above
            </p>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-8"
          >
            <div>
              <div className="flex justify-between items-center mb-4">
                <Label className="text-base font-medium">Width (meters)</Label>
                <span className="font-mono text-lg text-primary font-bold">{formData.width}m</span>
              </div>
              <Slider
                value={[formData.width]}
                onValueChange={(v) => handleSliderChange('width', v)}
                min={1}
                max={50}
                step={0.5}
                className="w-full"
                data-testid="width-slider"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <Label className="text-base font-medium">Length (meters)</Label>
                <span className="font-mono text-lg text-primary font-bold">{formData.length}m</span>
              </div>
              <Slider
                value={[formData.length]}
                onValueChange={(v) => handleSliderChange('length', v)}
                min={1}
                max={50}
                step={0.5}
                data-testid="length-slider"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <Label className="text-base font-medium">Available Height (meters)</Label>
                <span className="font-mono text-lg text-primary font-bold">{formData.height}m</span>
              </div>
              <Slider
                value={[formData.height]}
                onValueChange={(v) => handleSliderChange('height', v)}
                min={1}
                max={15}
                step={0.5}
                data-testid="height-slider"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Vertical space for trees and tall structures
              </p>
            </div>

            {/* Visual representation */}
            <Card className="bg-muted/30 border-dashed">
              <CardContent className="p-6">
                <div className="flex items-center justify-center gap-8">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Ground Area</p>
                    <p className="font-mono text-2xl font-bold text-primary">
                      {(formData.width * formData.length).toFixed(1)} m²
                    </p>
                  </div>
                  <div className="h-12 w-px bg-border" />
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Volume</p>
                    <p className="font-mono text-2xl font-bold text-secondary">
                      {(formData.width * formData.length * formData.height).toFixed(1)} m³
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <p className="text-muted-foreground">
              Choose the primary focus for your garden design:
            </p>
            
            <RadioGroup
              value={formData.aesthetic}
              onValueChange={(value) => setFormData(prev => ({ ...prev, aesthetic: value }))}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              {aestheticOptions.map((option) => (
                <Label
                  key={option.value}
                  htmlFor={option.value}
                  className={`cursor-pointer transition-all duration-300`}
                >
                  <Card
                    className={`h-full hover-lift ${
                      formData.aesthetic === option.value
                        ? option.color + ' border-2'
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`aesthetic-${option.value}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <RadioGroupItem
                          value={option.value}
                          id={option.value}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                            formData.aesthetic === option.value
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {option.icon}
                          </div>
                          <h3 className="font-serif text-lg font-semibold mb-1">
                            {option.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {option.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Label>
              ))}
            </RadioGroup>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Summary Card */}
            <Card className="card-organic">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Plan Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Garden Name</p>
                    <p className="font-medium">{formData.name || "Unnamed Garden"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">{formData.location || "Not specified"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Dimensions</p>
                    <p className="font-mono font-medium">
                      {formData.width}m × {formData.length}m × {formData.height}m
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Focus</p>
                    <p className="font-medium capitalize">{formData.aesthetic}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Solar Data Card */}
            {solarData && (
              <Card className="card-organic solar-metric">
                <CardHeader>
                  <CardTitle className="font-serif text-xl flex items-center gap-2">
                    <Sun className="w-5 h-5 text-secondary" />
                    Solar Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-white/50 rounded-xl">
                      <p className="text-sm text-muted-foreground mb-1">Summer Sun</p>
                      <p className="font-mono text-3xl font-bold text-primary">
                        {solarData.summer_sun_hours}h
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white/50 rounded-xl">
                      <p className="text-sm text-muted-foreground mb-1">Winter Sun</p>
                      <p className="font-mono text-3xl font-bold text-primary">
                        {solarData.winter_sun_hours}h
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4 text-center">
                    {solarData.optimal_orientation}
                  </p>
                </CardContent>
              </Card>
            )}

            <div className="bg-primary/5 rounded-xl p-6 border border-primary/20">
              <h4 className="font-serif text-lg font-semibold mb-2 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-secondary" />
                Ready to Generate
              </h4>
              <p className="text-sm text-muted-foreground">
                We will create a custom permaculture design based on your location&apos;s climate, 
                solar availability, and chosen aesthetic. This includes plant recommendations, 
                companion planting guilds, and seasonal care schedules.
              </p>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-6">
      {/* Header */}
      <div className="max-w-3xl mx-auto mb-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
        
        <h1 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-2">
          Create Your Garden Plan
        </h1>
        <p className="text-muted-foreground">
          Answer a few questions to get your personalized permaculture design.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="max-w-3xl mx-auto mb-10">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                    currentStep === step.number
                      ? 'bg-primary text-primary-foreground scale-110'
                      : currentStep > step.number
                      ? 'bg-secondary text-secondary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {currentStep > step.number ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    step.icon
                  )}
                </div>
                <span className={`text-xs mt-2 font-medium ${
                  currentStep >= step.number ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 md:w-24 h-0.5 mx-2 -mt-6 transition-colors duration-300 ${
                  currentStep > step.number ? 'bg-secondary' : 'bg-muted'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <Card className="max-w-3xl mx-auto card-organic">
        <CardContent className="p-8">
          <AnimatePresence mode="wait">
            {renderStep()}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-10 pt-6 border-t border-border">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="btn-secondary"
              data-testid="wizard-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {currentStep < 4 ? (
              <Button 
                onClick={handleNext}
                className="btn-primary"
                data-testid="wizard-next-btn"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="btn-primary bg-secondary hover:bg-secondary/90"
                data-testid="generate-plan-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Plan
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Get Out and Plant Checklist */}
      <div className="max-w-3xl mx-auto mt-8">
        <GetStartedChecklist user={user} />
      </div>
    </div>
  );
};

export default GardenWizard;
